<template>
  <!-- 可縮放大圖（zoom，2026-10-04《240507 真實透鏡》）：一張幾千萬像素的圖切成 Deep Zoom 磚放 R2，
       OpenSeadragon 只在進視窗時才載（動態 import），只抓畫面上看得到、對應倍率的那幾塊磚。
       第一眼是全圖；點一下才把滾輪與拖曳交給檢視器（不然捲頁經過時滾輪會被吃掉），Esc／「結束」／捲出視窗就還給頁面。
       磚與原圖都在 R2：只有 .dzi 與原圖登記在 media-manifest，磚跟著 .dzi 同名的 `_files/` 目錄走（docs/MEDIA.md）。 -->
  <section :id="id" class="work-stage work-zoom-stage">
    <div class="work-gallery-heading"><span class="work-section-label">{{ title }}</span><span>{{ size }}</span></div>
    <figure class="wz">
      <div ref="stage" class="wz-stage" :class="{ 'is-active': active, 'is-ready': ready, 'is-page': pageFull }" :style="{ aspectRatio: width + '/' + height }">
        <img v-if="src" class="wz-preview" :class="{ 'is-hidden': ready }" :src="src" :alt="alt" :width="width" :height="height" loading="lazy" decoding="async" />
        <div ref="viewerEl" class="wz-viewer" :aria-label="alt" role="img"></div>
        <button v-if="!active" type="button" class="wz-start" @click="activate">
          <span>{{ zh ? '點一下，放大看細節' : (coarse ? 'Tap to zoom into the detail' : 'Click to zoom into the detail') }}</span>
          <small>{{ coarse ? (zh ? '雙指放大，拖曳移動' : 'Pinch to zoom, drag to move') : (zh ? '滾輪放大，拖曳移動' : 'Scroll to zoom, drag to move') }}</small>
        </button>
        <div v-if="active" class="wz-tools" role="toolbar" :aria-label="zh ? '放大檢視工具' : 'Zoom controls'">
          <button type="button" @click="zoomBy(1.6)" :aria-label="zh ? '放大' : 'Zoom in'">＋</button>
          <button type="button" @click="zoomBy(1 / 1.6)" :aria-label="zh ? '縮小' : 'Zoom out'">－</button>
          <button type="button" @click="actual">100%</button>
          <button type="button" @click="home">{{ zh ? '全圖' : 'Fit' }}</button>
          <button type="button" @click="full">{{ isFull ? (zh ? '離開全螢幕' : 'Exit full screen') : (zh ? '全螢幕' : 'Full screen') }}</button>
          <button type="button" @click="deactivate">{{ zh ? '結束' : 'Done' }}</button>
          <output class="wz-pct" aria-live="polite">{{ pct }}</output>
        </div>
      </div>
      <figcaption>
        <span v-if="caption">{{ caption }}</span>
        <a v-if="original" :href="original" target="_blank" rel="noopener noreferrer" class="wz-download">{{ downloadLabel }} <svg class="work-icon-arrow" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path d="M4 12 12 4M6.5 4H12v5.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></a>
      </figcaption>
      <p v-if="note" class="work-loops-note">{{ note }}</p>
    </figure>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
const props = defineProps({ block: { type: Object, required: true }, zh: Boolean })
const b = props.block
const { id, title, src, alt, caption, note, original, width, height, tiles } = b
const size = `${Number(width).toLocaleString('en-US')} × ${Number(height).toLocaleString('en-US')}`
const downloadLabel = computed(() => {
  const mb = b.originalBytes ? (b.originalBytes / 1e6).toFixed(1) + ' MB' : ''
  return props.zh ? `下載原圖（JPG，${size}${mb ? '，' + mb : ''}）` : `Download the original (JPG, ${size}${mb ? ', ' + mb : ''})`
})
const stage = ref(null), viewerEl = ref(null)
const active = ref(false), ready = ref(false), isFull = ref(false), pageFull = ref(false), pct = ref(''), coarse = ref(false)
let viewer = null, io = null, out = null, loading = null
const reduced = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
// 預渲染與爬蟲不起檢視器（跟 WorkRecord 的 underlay 同一條規則），靜態 HTML 裡只有預覽圖
const robot = () => typeof navigator === 'undefined' || /bot|crawler|spider|headless|prerender/i.test(navigator.userAgent)

async function load() {
  if (viewer || loading) return loading
  loading = (async () => {
    const OSD = (await import('openseadragon')).default
    viewer = OSD({
      element: viewerEl.value,
      // DZI 寫在資料裡（不另外抓 .dzi）：R2 沒開 CORS，跨站 XHR 會被擋；磚用一般 <img> 載入就不需要 CORS；canvas／WebGL drawer 讀不到沒有 CORS 的圖（實測整片黑），所以用 html drawer。
      tileSources: { Image: { xmlns: 'http://schemas.microsoft.com/deepzoom/2008', Url: tiles.base, Format: tiles.format, Overlap: String(tiles.overlap), TileSize: String(tiles.tileSize), Size: { Width: String(width), Height: String(height) } } },
      drawer: 'html', crossOriginPolicy: false,
      showNavigationControl: false, showNavigator: false,
      visibilityRatio: 1, constrainDuringPan: true, minZoomImageRatio: 1, maxZoomPixelRatio: 2,
      animationTime: reduced() ? 0 : 0.9, springStiffness: 9, immediateRender: false, preserveImageSizeOnResize: true,
      gestureSettingsMouse: { scrollToZoom: true, clickToZoom: false, dblClickToZoom: true, dragToPan: true },
      gestureSettingsTouch: { pinchToZoom: true, dragToPan: true, flickEnabled: true, clickToZoom: false, dblClickToZoom: true },
    })
    viewer.setMouseNavEnabled(active.value)
    viewer.addHandler('tile-drawn', () => { ready.value = true })
    viewer.addHandler('open', () => { update() })
    viewer.addHandler('zoom', update)
    viewer.addHandler('animation', update)
    viewer.addHandler('animation-finish', update)
  })()
  return loading
}
function update() {
  if (!viewer?.viewport || !viewer.world.getItemCount()) return
  const z = viewer.viewport.viewportToImageZoom(viewer.viewport.getZoom(true))
  pct.value = Math.round(z * 100) + '%'
}
async function activate() {
  active.value = true
  await load()
  viewer.setMouseNavEnabled(true)
  viewerEl.value?.focus?.()
}
function deactivate() {
  active.value = false
  if (isFull.value) exitFull()
  viewer?.setMouseNavEnabled(false)
}
const zoomBy = f => { viewer?.viewport.zoomBy(f); viewer?.viewport.applyConstraints() }
const home = () => viewer?.viewport.goHome()
// 100%＝原圖一個像素對畫面一個 CSS 像素
const actual = () => { if (!viewer) return; viewer.viewport.zoomTo(viewer.viewport.imageToViewportZoom(1)); viewer.viewport.applyConstraints() }
// 全螢幕：整個舞台（含工具列）進 Fullscreen API；iPhone 的 Safari 沒有元素全螢幕，退回「固定蓋滿視窗」。
// OpenSeadragon 每一格都量容器尺寸，舞台一變大它自己重新排。
const setPage = on => { pageFull.value = on; isFull.value = on; document.documentElement.style.overflow = on ? 'hidden' : '' }
function exitFull() { if (document.fullscreenElement) document.exitFullscreen?.(); if (pageFull.value) setPage(false) }
async function full() {
  if (isFull.value) return exitFull()
  const el = stage.value
  if (el?.requestFullscreen) { try { await el.requestFullscreen(); return } catch { /* 退回固定蓋滿 */ } }
  setPage(true)
}
const onFs = () => { isFull.value = document.fullscreenElement === stage.value || pageFull.value }
const onKey = e => { if (e.key !== 'Escape') return; if (pageFull.value) setPage(false); else if (active.value && !isFull.value) deactivate() }

onMounted(() => {
  coarse.value = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches
  if (robot() || typeof IntersectionObserver === 'undefined') return
  io = new IntersectionObserver(es => { if (es.some(e => e.isIntersecting)) { io.disconnect(); load() } }, { rootMargin: '600px 0px' })
  io.observe(stage.value)
  // 捲出視窗就把滾輪還給頁面
  out = new IntersectionObserver(es => { for (const e of es) if (!e.isIntersecting && active.value && !isFull.value) deactivate() }, { threshold: 0 })
  out.observe(stage.value)
  addEventListener('keydown', onKey)
  document.addEventListener('fullscreenchange', onFs)
})
onBeforeUnmount(() => { io?.disconnect(); out?.disconnect(); removeEventListener('keydown', onKey); document.removeEventListener('fullscreenchange', onFs); if (pageFull.value) setPage(false); viewer?.destroy(); viewer = null })
</script>

<style>
.work-zoom-stage .wz { margin: 0; }
.wz-stage { position: relative; width: 100%; max-height: 82vh; background: #000; overflow: hidden; }
.wz-stage:fullscreen { max-height: none; width: 100vw; height: 100vh; aspect-ratio: auto !important; }
.wz-stage.is-page { position: fixed; inset: 0; z-index: 1000; max-height: none; width: 100vw; height: 100dvh; aspect-ratio: auto !important; }
.wz-preview { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: contain; transition: opacity .4s; }
.wz-preview.is-hidden { opacity: 0; pointer-events: none; }
.wz-viewer { position: absolute; inset: 0; outline: none; }
.wz-stage:not(.is-active) .wz-viewer { pointer-events: none; }
.wz-start { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; gap: 4px; padding: 0 0 28px; border: 0; background: linear-gradient(to top, rgba(0,0,0,.55), rgba(0,0,0,0) 38%); color: #f4f4f2; cursor: zoom-in; font: inherit; }
.wz-start span { font-size: 15px; letter-spacing: .04em; padding: 8px 16px; border: 1px solid rgba(244,244,242,.5); border-radius: 999px; background: rgba(8,8,8,.55); }
.wz-start small { font-size: 12px; color: rgba(244,244,242,.75); }
.wz-start:hover span, .wz-start:focus-visible span { border-color: var(--gold, #d9c267); color: var(--gold, #d9c267); }
.wz-tools { position: absolute; right: 12px; bottom: 12px; z-index: 5; display: flex; flex-wrap: wrap; gap: 6px; align-items: center; justify-content: flex-end; max-width: calc(100% - 24px); }
.wz-tools button { min-width: 36px; height: 34px; padding: 0 10px; border: 1px solid rgba(244,244,242,.35); border-radius: 999px; background: rgba(8,8,8,.72); color: #f4f4f2; font: inherit; font-size: 13px; cursor: pointer; }
.wz-tools button:hover, .wz-tools button:focus-visible { border-color: var(--gold, #d9c267); color: var(--gold, #d9c267); outline: none; }
.wz-pct { min-width: 46px; padding: 0 8px; font-size: 12px; font-variant-numeric: tabular-nums; color: rgba(244,244,242,.8); text-align: right; }
.work-zoom-stage figcaption { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 8px 24px; margin-top: 12px; font-size: 13px; color: var(--ink-2, #b4b4b0); }
.wz-download { color: var(--gold, #d9c267); text-decoration: none; white-space: nowrap; }
.wz-download:hover { text-decoration: underline; }
/* 手機：16:9 的圖在 390 寬只有 200 出頭高，舞台改 4:3 留出放大的空間；工具列攤平在底部 */
@media (max-width: 640px) {
  .wz-stage:not(.is-page):not(:fullscreen) { aspect-ratio: 4 / 3 !important; }
  .wz-tools { left: 8px; right: 8px; bottom: 8px; max-width: none; gap: 4px; justify-content: center; }
  .wz-tools button { min-width: 30px; height: 30px; padding: 0 7px; font-size: 12px; }
  .wz-pct { min-width: 0; padding: 0 2px; font-size: 11px; }
  .wz-start { padding-bottom: 14px; }
  .wz-start span { font-size: 14px; }
}
</style>
